
import React, { useState, useEffect } from 'react';
import { HashRouter, Routes, Route } from 'react-router-dom';
import Sidebar from './components/Sidebar';
// Use the ultra-optimized dashboard with unified endpoint and single API call
import Dashboard from './components/DashboardFinalOptimized';
import EmployeesPageDB from './components/EmployeesPageDB';
import ProductsPageDB from './components/ProductsPageDB';
import SettingsPage from './components/SettingsPage';
import StationsPageDB from './components/StationsPageDB';
import ReportsPage from './components/ReportsPageImproved'; // Updated to use improved ReportsPage
import DiagnosticsPage from './components/DiagnosticsPage';
import UserManual from './components/UserManualEnhanced';
import ProductionModePanel from './components/ProductionModePanel';
import { NAVIGATION_ITEMS } from './constants';
import { Product, Line } from './types';
import { AppProvider } from './contexts/AppContext';
import { InterfaceProvider } from './contexts/InterfaceContext';
import { PreferencesProvider } from './contexts/PreferencesContext';
import { ProductionProvider } from './contexts/ProductionContext';
import { ProductGroupProvider } from './contexts/ProductGroupContext';
import { ProductionModeProvider } from './contexts/ProductionModeContext';


const PlaceholderPage: React.FC<{ title: string }> = ({ title }) => (
  <div className="p-8 text-2xl font-semibold text-slate-700">
    {title}
  </div>
);

const ContentArea: React.FC = () => {
  return (
    <Routes>
      <Route path="/" element={<Dashboard />} />
      <Route path="/stations" element={<StationsPageDB />} />
      <Route path="/employees" element={<EmployeesPageDB />} />
      <Route path="/products" element={<ProductsPageDB />} />
      <Route path="/reports" element={<ReportsPage />} />
      <Route path="/settings" element={<SettingsPage />} />
      <Route path="/diagnostics" element={<DiagnosticsPage />} />
      <Route path="/manual" element={<UserManual />} />
      <Route path="*" element={<Dashboard />} />
    </Routes>
  );
}

const App: React.FC = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    // Load sidebar state from localStorage
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved === 'true';
  });
  const [activePageId, setActivePageId] = useState(NAVIGATION_ITEMS[0].id);
  const [isAppInitialized, setIsAppInitialized] = useState(true); // App is always initialized now since data is in DB
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null); // null = checking, true/false = determined
  const [hasDefaultUser, setHasDefaultUser] = useState<boolean | null>(null);

  const handleToggleSidebar = () => {
    const newState = !isSidebarCollapsed;
    setIsSidebarCollapsed(newState);
    // Save to localStorage
    localStorage.setItem('sidebarCollapsed', newState.toString());
  };

  useEffect(() => {
    const updateActivePage = () => {
      let currentPathFromHash = window.location.hash;
      if (currentPathFromHash.startsWith('#')) {
        currentPathFromHash = currentPathFromHash.substring(1);
      }
      if (currentPathFromHash === '' || currentPathFromHash === '/') {
        currentPathFromHash = '/';
      }
      const activeNavItem = NAVIGATION_ITEMS.find(item => item.path === currentPathFromHash);
      if (activeNavItem) {
        setActivePageId(activeNavItem.id);
      } else if (currentPathFromHash === '/') {
        setActivePageId(NAVIGATION_ITEMS.find(item => item.path === '/')?.id || NAVIGATION_ITEMS[0].id);
      }
    };
    updateActivePage();
    window.addEventListener('hashchange', updateActivePage);
    return () => {
      window.removeEventListener('hashchange', updateActivePage);
    };
  }, []);

  const handleNavItemClick = (itemId: string) => {
    setActivePageId(itemId);
  };

  // Check authentication status on mount
  useEffect(() => {
    const checkAuth = async () => {
      // First check if user is logged in
      const storedUser = localStorage.getItem('currentUser');
      if (storedUser) {
        setIsAuthenticated(true);
        return;
      }

      // Check if there's a default user
      try {
        const response = await fetch('/api/users/default');
        const data = await response.json();
        
        if (data.message === 'success' && data.data) {
          setHasDefaultUser(true);
          setIsAuthenticated(true); // Allow access with default user
        } else {
          setHasDefaultUser(false);
          setIsAuthenticated(false); // No default user, require login
        }
      } catch (error) {
        console.error('Error checking authentication:', error);
        setHasDefaultUser(false);
        setIsAuthenticated(false);
      }
    };

    checkAuth();
  }, []);

  // Loading state while checking authentication
  if (isAuthenticated === null) {
    return (
      <div className="flex h-screen items-center justify-center bg-slate-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <InterfaceProvider>
      <AppProvider isInitialized={isAppInitialized}>
        <PreferencesProvider>
          <ProductionProvider>
            <ProductGroupProvider>
              <ProductionModeProvider>
                <HashRouter>
                <div className="flex h-screen bg-slate-100">
                  <Sidebar
                    isCollapsed={isSidebarCollapsed}
                    onToggleCollapse={handleToggleSidebar}
                    activeItemId={activePageId}
                    onNavItemClick={handleNavItemClick}
                    onAuthChange={setIsAuthenticated}
                    requireLogin={!hasDefaultUser}
                  />
                  {isAuthenticated ? (
                    <>
                      <main className="flex-1 overflow-y-auto">
                        <ContentArea />
                      </main>
                      <ProductionModePanel />
                    </>
                  ) : (
                    <div className="flex-1 flex items-center justify-center bg-slate-100">
                      <div className="text-center">
                        <svg className="mx-auto h-16 w-16 text-slate-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                        <h2 className="text-2xl font-semibold text-slate-700 mb-2">Authentication Required</h2>
                        <p className="text-slate-600">Please login to access the Yield Track Dashboard</p>
                      </div>
                    </div>
                  )}
                </div>
                </HashRouter>
              </ProductionModeProvider>
            </ProductGroupProvider>
          </ProductionProvider>
        </PreferencesProvider>
      </AppProvider>
    </InterfaceProvider>
  );
};

export default App;